import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { logout } from '../store/session';
import { csrfFetch } from '../utils/csrfFetch';
import useTimeoutSettings from '../hooks/useTimeoutSettings';
import { FaClock, FaSignOutAlt, FaRedoAlt } from 'react-icons/fa';

const SessionTimeoutWarning = ({
  warningTime = 5 * 60 * 1000, // 5 minutes warning before timeout
  onContinue = () => {} // Callback when user clicks "Continue Session"
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExtending, setIsExtending] = useState(false);
  const dispatch = useDispatch();

  // Use our custom hook for timeout settings
  const { timeoutHours } = useTimeoutSettings();

  // Calculate timeout duration in milliseconds
  const timeoutDuration = timeoutHours * 60 * 60 * 1000;

  useEffect(() => {
    let warningTimeout;
    let logoutTimeout;
    let countdownInterval;

    const performLogout = () => {
      clearInterval(countdownInterval);
      dispatch(logout(true)); // Pass true to indicate auto-logout
    };

    // Set timeout to show warning
    warningTimeout = setTimeout(() => {
      setShowWarning(true);
      setTimeLeft(Math.floor(warningTime / 1000)); // Convert to seconds
    }, timeoutDuration - warningTime);

    // Set timeout for actual logout
    logoutTimeout = setTimeout(performLogout, timeoutDuration);

    // Countdown timer when warning is shown
    if (showWarning) {
      countdownInterval = setInterval(() => {
        setTimeLeft(prevTime => {
          if (prevTime <= 1) {
            performLogout();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }

    // Cleanup function
    return () => {
      clearTimeout(warningTimeout);
      clearTimeout(logoutTimeout);
      if (countdownInterval) clearInterval(countdownInterval);
    };
  }, [dispatch, showWarning, timeoutDuration, warningTime]);

  const handleContinue = async () => {
    setIsExtending(true);
    try {
      // Call the refresh-session endpoint to extend the session on the server
      const response = await csrfFetch('/api/auth/refresh-session', {
        method: 'POST',
      });

      if (response.ok) {
        setShowWarning(false);
        setTimeLeft(0);
        onContinue();
      } else {
        console.error('Failed to refresh session');
        dispatch(logout());
      }
    } catch (error) {
      console.error('Error refreshing session:', error);
      dispatch(logout());
    } finally {
      setIsExtending(false);
    }
  };

  const handleLogout = () => {
    setShowWarning(false);
    setTimeLeft(0);
    dispatch(logout());
  };

  if (!showWarning) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Calculate progress percentage for the timer
  const progressPercentage = (timeLeft / (warningTime / 1000)) * 100;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex items-center">
          <FaClock className="text-2xl mr-3" />
          <h2 className="text-xl font-bold">Session Timeout Warning</h2>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-6">
            <p className="text-gray-700 mb-4">
              Your session is about to expire due to inactivity. For security reasons, you will be automatically logged out in:
            </p>

            {/* Timer display */}
            <div className="flex flex-col items-center">
              <div className="text-4xl font-bold text-blue-700 mb-2">
                {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
              </div>

              {/* Progress bar */}
              <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-blue-600 transition-all duration-1000 ease-linear"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-blue-800">
              To continue your session, click the "Continue Session" button below. Otherwise, you will be logged out automatically when the timer expires.
            </p>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              onClick={handleLogout}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center"
            >
              <FaSignOutAlt className="mr-2" />
              Logout Now
            </button>
            <button
              onClick={handleContinue}
              disabled={isExtending}
              className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center ${
                isExtending ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              <FaRedoAlt className={`mr-2 ${isExtending ? 'animate-spin' : ''}`} />
              {isExtending ? 'Extending...' : 'Continue Session'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
